/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { createPortal } from "react-dom";
import { AiOutlineLoading } from "react-icons/ai";

import { getCustomCity, getLevelIds } from "./helper";
import { useCityContext } from "../../../../../context/levels/levelsContext";
import { MyButton, Pagination } from "../../../component/Component";
import { addOneLevel } from "../../../../../middlewares/breadCrumb/BreadCrumb";
import { useAdminContext } from "../../../../../context/admin/context/AdminContext";
import {
  pushLevel,
  pushEditLevel,
  pushCityDashboard,
} from "../../../../../middlewares/sysNavigation/SysNavigation";

const editLevelRoute = "/level/edit";
const addContentRoute = "/dosee/data";
const dashboardLevelRoute = "/city/dashboard";

const Overview = () => {
  const navigate = useNavigate();
  const { cities, fetchCities, count } = useCityContext();
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(150);
  const [query, setQuery] = useState("");

  useEffect(() => {
    if (!cities) fetchCities(page, pageSize);
  }, [cities]);

  const baseCities = useMemo(() => {
    if (!cities || cities.length === 0) return [];
    const prioritized = cities
      .filter((c) => c?.priority === 1)
      .sort((a, b) => (a?.name > b?.name ? 1 : a?.name < b?.name ? -1 : 0));
    const others = cities
      .filter((c) => c?.priority === 0)
      .sort((a, b) => (a?.name > b?.name ? 1 : a?.name < b?.name ? -1 : 0));
    return [...prioritized, ...others];
  }, [cities]);

  const q = query.trim().toLowerCase();
  const displayCities = useMemo(() => {
    if (!q) return baseCities;
    return baseCities.filter((c) => {
      const name = String(c?.name || "").toLowerCase();
      const country = String(c?.country_name || "").toLowerCase();
      const level = String(c?.level || "").toLowerCase();
      return name.includes(q) || country.includes(q) || level.includes(q);
    });
  }, [baseCities, q]);

  const prioritizedCount =
    displayCities?.filter((c) => c?.priority === 1)?.length || 0;

  return (
    <div className="h-full overflow-y-auto bg-gray-50 p-4 rounded-b-xl">
      <div className="relative">
        {/* soften accent glow */}
        <div className="absolute -inset-x-2 -top-2 h-20 bg-gradient-to-r from-emerald-400/15 via-amber-400/15 to-fuchsia-400/15 blur-2xl rounded-3xl" />
        <div className="relative rounded-3xl border border-gray-200 bg-white/90 shadow-sm backdrop-blur">
          <header className="flex flex-col gap-4 p-5 sm:flex-row sm:items-center sm:justify-between">
            <div>
              <h1 className="text-base font-semibold tracking-tight text-gray-900">
                City Directory
              </h1>
              <p className="mt-0.5 text-xs text-gray-600">
                Manage all cities across levels. Prioritized cities appear
                first.
              </p>
            </div>
            <div className="flex w-full flex-col gap-3 sm:w-auto sm:flex-row sm:items-center">
              <div className="flex items-center gap-2">
                <StatChip label="Total" value={count || 0} />
                <StatChip
                  label="Prioritized"
                  value={prioritizedCount}
                  tone="emerald"
                />
              </div>
            </div>
          </header>

          <div className="px-5 pb-4">
            <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
              <SearchBar value={query} onChange={setQuery} />
              {/* <div className="hidden sm:block">
                <Pagination
                  page={page}
                  setPage={setPage}
                  pageSize={pageSize * 4}
                  total_items={count}
                  setPageSize={setPageSize}
                  callbackFunction={(p, size) => fetchCities(p, pageSize)}
                />
              </div> */}
            </div>
          </div>

          <div className="px-3 pb-5">
            <div className="overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-sm">
              <div className="relative max-h-[60vh] overflow-auto">
                <table className="min-w-full text-sm">
                  <thead className="sticky top-0 z-10 bg-white/90 backdrop-blur">
                    <tr className="text-left text-xs uppercase tracking-wide text-gray-500">
                      <th className="px-4 py-3 font-semibold">City</th>
                      <th className="px-4 py-3 font-semibold">Level</th>
                      <th className="px-4 py-3 font-semibold">Priority</th>
                      <th className="px-4 py-3 font-semibold text-center">
                        Manage
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {displayCities && displayCities?.length > 0 ? (
                      displayCities?.map((city, idx) => (
                        <tr
                          key={`${city?.name}-${idx}`}
                          className="group border-t border-gray-100 hover:bg-gray-50/60"
                        >
                          <td className="px-4 py-3 align-middle">
                            <div className="flex items-center gap-3">
                              <div className="grid h-8 w-8 place-items-center rounded-lg bg-gray-900 text-white text-[10px] font-bold shadow-sm">
                                {city?.name?.slice(0, 2)?.toUpperCase() || "CI"}
                              </div>
                              <div className="min-w-0">
                                <div className="truncate font-semibold text-gray-900">
                                  {city?.name}
                                </div>
                                <div className="truncate text-[11px] text-gray-500">
                                  {city?.country_name || ""}
                                </div>
                              </div>
                            </div>
                          </td>

                          <td className="px-4 py-3 align-middle">
                            <Badge tone="slate">{city?.level}</Badge>
                          </td>

                          <td className="px-4 py-3 align-middle">
                            {city?.priority === 1 ? (
                              <Badge tone="emerald">On Top</Badge>
                            ) : (
                              <Badge tone="gray">Standard</Badge>
                            )}
                          </td>

                          <td className="px-4 py-3 align-middle">
                            <div className="flex justify-center">
                              <CallToAction city={city} navigate={navigate} />
                            </div>
                          </td>
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td
                          className="px-4 py-6 text-center text-gray-500"
                          colSpan={4}
                        >
                          {q
                            ? "No cities match your search."
                            : "No cities found."}
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>

              <div className="border-t border-gray-100 px-4 py-3">
                <div className="flex items-center justify-end">
                  <Pagination
                    page={page}
                    setPage={setPage}
                    pageSize={pageSize * 4}
                    total_items={count}
                    setPageSize={setPageSize}
                    callbackFunction={(p, size) => fetchCities(p, pageSize)}
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

const SearchBar = ({ value, onChange }) => {
  const inputRef = useRef(null);
  return (
    <div className="w-full sm:w-80">
      <label className="sr-only" htmlFor="city-search">
        Search cities
      </label>
      <div className="relative">
        <input
          id="city-search"
          ref={inputRef}
          type="text"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder="Search by city, country, or level…"
          className="w-full rounded-lg border border-gray-300 bg-white py-2 pl-3 pr-8 text-sm text-gray-900 placeholder:text-gray-400 shadow-sm focus:border-emerald-500 focus:outline-none focus:ring-2 focus:ring-emerald-200"
        />
        {value ? (
          <button
            type="button"
            onClick={() => {
              onChange("");
              inputRef.current?.focus();
            }}
            className="absolute right-2 top-1/2 -translate-y-1/2 rounded-md px-2 text-[11px] font-semibold text-gray-600 hover:bg-gray-100"
            aria-label="Clear search"
          >
            ✕
          </button>
        ) : null}
      </div>
      <p className="mt-1 text-[11px] text-gray-500">
        Tip: Try “Nairobi”, “Kenya”, or “Third”.
      </p>
    </div>
  );
};

const Badge = ({ tone = "gray", children }) => {
  const tones = {
    gray: "text-gray-700 bg-gray-100 ring-gray-200",
    slate: "text-slate-700 bg-slate-100 ring-slate-200",
    emerald: "text-emerald-700 bg-emerald-50 ring-emerald-200",
  };
  return (
    <span
      className={[
        "inline-flex items-center rounded-lg px-2.5 py-1 text-[11px] font-semibold ring-1",
        tones[tone] || tones.gray,
      ].join(" ")}
    >
      {children}
    </span>
  );
};

const StatChip = ({ label, value, tone = "slate" }) => {
  const tones = {
    slate: "from-slate-900 to-slate-700",
    emerald: "from-emerald-600 to-emerald-500",
  };
  return (
    <div className="flex items-center gap-2">
      <span className="text-[11px] text-gray-600">{label}</span>
      <span
        className={[
          "grid place-items-center rounded-md px-2.5 py-1 text-[11px] font-semibold text-white shadow",
          "bg-gradient-to-br",
          tones[tone] || tones.slate,
        ].join(" ")}
      >
        {typeof value === "number" ? value.toLocaleString() : value}
      </span>
    </div>
  );
};

const CallToAction = ({ city, navigate }) => {
  const { sysAdmin } = useAdminContext();
  const { addPriority, cities, setCities, updateCityStatus } = useCityContext();
  const { name, level, priority, status } = city;
  const [loading, setLoading] = useState(false);
  const [statusLoading, setStatusLoading] = useState(false);

  const {
    level_id,
    first_level_id,
    third_level_id,
    fourth_level_id,
    second_level_id,
  } = getLevelIds({ city });

  const customCity = useMemo(
    () =>
      getCustomCity({
        city,
        first_level_id,
        third_level_id,
        second_level_id,
        fourth_level_id,
      }),
    [city, first_level_id, third_level_id, second_level_id, fourth_level_id]
  );

  const [modal, setModal] = useState({
    open: false,
    action: null,
    title: "",
    description: "",
  });

  const closeModal = () =>
    setModal({ open: false, action: null, title: "", description: "" });

  const confirm = async () => {
    if (modal.action === "priority") {
      await handleTogglePriority();
      closeModal();
      return;
    }

    if (modal.action === "status") {
      await handleToggleStatus(level);
      closeModal();
      return;
    }

    if (modal.action === "edit") {
      pushEditLevel({
        data: city,
        level: city?.level,
        country_id: city?.country_id,
      });
      closeModal();
      navigate(editLevelRoute);
      return;
    }
    if (modal.action === "dashboard") {
      pushCityDashboard([level, customCity]);
      closeModal();
      navigate(dashboardLevelRoute);
      return;
    }
    if (modal.action === "content") {
      addOneLevel({
        id: level_id,
        name: name,
        route: "/levels/" + level.toString().toLowerCase(),
        level: level,
        data: { data: customCity },
      });
      pushLevel({
        level: {
          id: level_id,
          name: name,
          level: level,
          data: customCity,
          route: "/levels/" + level.toString().toLowerCase(),
        },
      });
      closeModal();
      navigate(addContentRoute);
      return;
    }
  };

  const handleToggleStatus = async () => {
    setStatusLoading(true);
    const res = await updateCityStatus({
      level: level,
      id: level_id,
      currentStatus: status,
    });

    const rule = status === 1 ? 0 : 1;

    if (res && res.status === 200) {
      setCities((prevCities) => {
        if (!prevCities) return [];

        const key = getKey();

        const updated = prevCities.map((city) =>
          city[key] === level_id ? { ...city, status: rule } : city
        );

        return updated.sort((a, b) => b.status - a.status);
      });
    }
    setStatusLoading(false);
  };

  const getKey = () => {
    let key = "first_level_id";
    if (String(level) === "First") key = "first_level_id";
    else if (String(level) === "Second") key = "second_level_id";
    else if (String(level) === "Third") key = "third_level_id";
    else if (String(level) === "Fourth") key = "fourth_level_id";

    return key;
  };

  const handleTogglePriority = async () => {
    const key = getKey();

    setLoading(true);
    const rule = priority === 0 ? 1 : 0;
    const { status } = await addPriority({ key, id: level_id, priority: rule });

    if (status === 200) {
      for (let j = 0; j < cities?.length; j++) {
        if (cities[j][key] === level_id) {
          cities[j].priority = rule;
          setCities(
            cities
              ?.sort((a, b) =>
                a?.priority > b?.priority
                  ? -1
                  : a?.priority < b?.priority
                  ? 1
                  : 0
              )
              ?.map((x) => x)
          );
          break;
        }
      }
    }
    setLoading(false);
  };

  const isMainRole = () => sysAdmin?.role === "super_admin";

  return (
    <>
      <div
        className={[
          "inline-flex w-full max-w-[760px] flex-wrap items-center justify-center gap-2",
          "rounded-2xl border border-gray-200 bg-white/70 px-3 py-2 shadow-sm",
        ].join(" ")}
      >
        {isMainRole() ? (
          <MyButton
            onClick={() =>
              setModal({
                open: true,
                action: "priority",
                title: priority ? "Remove priority?" : "Prioritize this city?",
                description: priority
                  ? "This city will no longer appear at the top of lists."
                  : "This city will be surfaced at the top where applicable.",
              })
            }
            className={`${
              priority === 0
                ? "bg-gradient-to-br from-orange-700 to-orange-600 hover:from-orange-800 hover:to-orange-700"
                : "bg-gradient-to-br from-purple-700 to-purple-600 hover:from-purple-800 hover:to-purple-700"
            } rounded-lg px-3 py-2 text-xs font-semibold`}
          >
            {loading ? (
              <span className="inline-flex items-center gap-2">
                <AiOutlineLoading className="animate-spin text-white" />
                Updating…
              </span>
            ) : priority !== 0 ? (
              "ON TOP"
            ) : (
              "PUSH ON TOP"
            )}
          </MyButton>
        ) : null}

        {isMainRole() && (
          <MyButton
            title="Edit"
            className="rounded-lg bg-gradient-to-br from-blue-700 to-blue-600 hover:from-blue-800 hover:to-blue-700 px-3 py-2 text-xs font-semibold"
            onClick={() =>
              setModal({
                open: true,
                action: "edit",
                title: "Edit this city?",
                description: "You will be redirected to the city edit page.",
              })
            }
          />
        )}

        <MyButton
          title="Dashboard"
          className="rounded-lg bg-gradient-to-br from-gray-800 to-gray-700 hover:from-gray-900 hover:to-gray-800 px-3 py-2 text-xs font-semibold"
          onClick={() =>
            setModal({
              open: true,
              action: "dashboard",
              title: "Open city dashboard?",
              description: "You will be redirected to the dashboard view.",
            })
          }
        />

        <MyButton
          title="Content"
          className="rounded-lg bg-gradient-to-br from-emerald-700 to-emerald-600 hover:from-emerald-800 hover:to-emerald-700 px-3 py-2 text-xs font-semibold"
          onClick={() =>
            setModal({
              open: true,
              action: "content",
              title: "Go to content?",
              description:
                "You will be redirected to manage content for this city.",
            })
          }
        />

        <MyButton
          title={status === 1 ? "Active" : "Hidden"}
          className={`rounded-lg bg-gradient-to-br  px-3 py-2 text-xs font-semibold ${
            status !== 1
              ? "from-amber-700 to-amber-600 hover:from-amber-800 hover:to-amber-700"
              : "from-teal-700 to-teal-600 hover:from-teal-800 hover:to-teal-700"
          }`}
          onClick={() =>
            setModal({
              open: true,
              action: "status",
              title: `Make ${city.name} Active on hidden?`,
              description: `${
                status === 1
                  ? "This city will no longer appear on the website."
                  : "This city will now appear on the website."
              }`,
            })
          }
        />

        <div className="mx-2 h-4 w-px bg-gray-200" />

        <SmallAction
          label="View"
          onClick={() =>
            setModal({
              open: true,
              action: "dashboard",
              title: "Open city dashboard?",
              description: "Open a quick dashboard view for this city.",
            })
          }
        />
        {isMainRole() && (
          <SmallAction
            label={priority ? "Demote" : "Promote"}
            accent={priority ? "purple" : "orange"}
            onClick={() =>
              setModal({
                open: true,
                action: "priority",
                title: priority ? "Remove priority?" : "Prioritize this city?",
                description: priority
                  ? "This city will no longer appear at the top of lists."
                  : "This city will be surfaced at the top where applicable.",
              })
            }
          />
        )}
        <SmallAction
          label="Content+"
          accent="emerald"
          onClick={() =>
            setModal({
              open: true,
              action: "content",
              title: "Go to content?",
              description: "Jump straight to content management for this city.",
            })
          }
        />
      </div>

      <ConfirmModal
        open={modal.open}
        title={modal.title}
        description={modal.description}
        confirmText="Proceed"
        cancelText="Cancel"
        onConfirm={confirm}
        onClose={closeModal}
      />
    </>
  );
};

const SmallAction = ({ label, accent = "slate", onClick }) => {
  const accents = {
    slate: "bg-slate-100 text-slate-700 hover:bg-slate-200",
    orange: "bg-orange-100 text-orange-700 hover:bg-orange-200",
    purple: "bg-purple-100 text-purple-700 hover:bg-purple-200",
    emerald: "bg-emerald-100 text-emerald-700 hover:bg-emerald-200",
  };
  return (
    <button
      type="button"
      onClick={onClick}
      className={[
        "rounded-md px-2.5 py-1 text-[11px] font-semibold transition",
        accents[accent] || accents.slate,
      ].join(" ")}
    >
      {label}
    </button>
  );
};

const ConfirmModal = ({
  open,
  title,
  description,
  confirmText = "Confirm",
  cancelText = "Cancel",
  loading = false,
  onConfirm,
  onClose,
}) => {
  const confirmRef = useRef(null);

  useEffect(() => {
    if (!open) return;
    const prevOverflow = document.documentElement.style.overflow;
    document.documentElement.style.overflow = "hidden";
    const t = setTimeout(() => confirmRef.current?.focus?.(), 0);
    return () => {
      document.documentElement.style.overflow = prevOverflow;
      clearTimeout(t);
    };
  }, [open]);

  if (!open) return null;

  // DESIGN-ONLY: darker, subtler scrim + toned-down ambient light kept INSIDE the dialog (not full-screen)
  const modal = (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="confirm-modal-title"
      aria-describedby="confirm-modal-desc"
      className="fixed inset-0 z-[2147483647] isolate flex items-center justify-center p-4"
    >
      {/* Scrim: darker and less distracting */}
      <div
        className="absolute inset-0 bg-black/70 backdrop-blur-[2px]"
        onClick={onClose}
        aria-hidden="true"
      />

      {/* Dialog */}
      <div className="relative w-full max-w-md">
        {/* Subtle top accent line */}
        <div
          className="absolute inset-x-0 top-0 h-1 rounded-t-2xl"
          aria-hidden
        />

        <div className="relative rounded-2xl border border-gray-200 bg-white/95 shadow-2xl ring-1 ring-black/5 supports-[backdrop-filter]:bg-white/85">
          {/* Very soft ambient inside the card only (low opacity) */}
          <div
            aria-hidden
            className="pointer-events-none absolute -inset-px rounded-2xl bg-gradient-to-tr from-emerald-300/12 via-amber-300/12 to-fuchsia-300/12 blur-lg"
          />

          <div className="relative px-5 pt-5">
            <h3
              id="confirm-modal-title"
              className="text-base font-semibold text-gray-900"
            >
              {title}
            </h3>
            {description ? (
              <p id="confirm-modal-desc" className="mt-1 text-sm text-gray-600">
                {description}
              </p>
            ) : null}
          </div>

          <div className="relative px-5 pb-5 pt-4 flex items-center justify-end gap-2">
            <button
              type="button"
              onClick={onClose}
              className="rounded-lg border border-gray-200 bg-white px-3 py-2 text-xs font-semibold text-gray-700 hover:bg-gray-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-emerald-500"
            >
              {cancelText}
            </button>
            <button
              type="button"
              ref={confirmRef}
              disabled={loading}
              onClick={onConfirm}
              className={[
                "rounded-lg px-3 py-2 text-xs font-semibold text-white shadow focus:outline-none focus-visible:ring-2 focus-visible:ring-emerald-500",
                "bg-gradient-to-br from-gray-900 to-gray-700 hover:from-black hover:to-gray-800",
                loading ? "opacity-80 cursor-not-allowed" : "",
              ].join(" ")}
            >
              {loading ? (
                <span className="inline-flex items-center gap-2">
                  <svg
                    className="h-4 w-4 animate-spin"
                    viewBox="0 0 24 24"
                    fill="none"
                  >
                    <circle
                      className="opacity-25"
                      cx="12"
                      cy="12"
                      r="10"
                      stroke="white"
                      strokeWidth="4"
                    />
                    <path
                      className="opacity-75"
                      fill="white"
                      d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z"
                    />
                  </svg>
                  Please wait…
                </span>
              ) : (
                confirmText
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );

  return createPortal(modal, document.body);
};

export default Overview;
